import React, { useEffect, useState, useMemo } from "react";
import { useApiClient } from "../../../api";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { AddCircle, Delete, ExpandMore } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import AreYouSureDialog from "../../../shared-components/AreYouSureDialog";

const MultiSelectDialog = ({
  open,
  onClose,
  title,
  options,
  selected,
  getOptionLabel,
  onSave,
}) => {
  const [localSelected, setLocalSelected] = useState([]);

  useEffect(() => {
    setLocalSelected(selected || []);
  }, [selected, open]);

  const isSelected = (option) =>
    localSelected.some((item) => item.id === option.id);

  const handleToggle = (option) => {
    if (isSelected(option)) {
      setLocalSelected((prev) => prev.filter((item) => item.id !== option.id));
    } else {
      setLocalSelected((prev) => [...prev, option]);
    }
  };

  const handleSave = () => {
    onSave(localSelected);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1}>
          {options.map((option) => (
            <FormControlLabel
              key={option.id}
              control={
                <Checkbox
                  checked={isSelected(option)}
                  onChange={() => handleToggle(option)}
                />
              }
              label={getOptionLabel(option)}
            />
          ))}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>

        <Button variant="contained" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const RoleAssignmentAccordion = ({
  roleAssignment,
  availableEducationLevels,
  availableSubjects,
  onDelete,
}) => {
  const ApiClient = useApiClient();
  const [dialogOpen, setDialogOpen] = useState(false);

  const [selectedSubjects, setSelectedSubjects] = useState(
    roleAssignment.subjects || [],
  );

  const [selectedEducationLevels, setSelectedEducationLevels] = useState(
    roleAssignment.educationLevels || [],
  );

  const updateRoleAssignment = async (subjects, educationLevels) => {
    try {
      const result = await ApiClient.users.updateRoleAssignment({
        roleAssignment: roleAssignment.id,
        subjects: subjects.map((subject) => subject.id),
        educationLevels: educationLevels.map((level) => level.id),
      });
      if (result.success) {
        enqueueSnackbar("Successfully updated role.", {
          variant: "success",
          autoHideDuration: 3000,
        });
        setSelectedSubjects(subjects);
        setSelectedEducationLevels(educationLevels);
      } else {
        enqueueSnackbar(result.message || "Failed to update role", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Switching error:", error);
      enqueueSnackbar("Error updating role. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const deleteRoleAssignment = async () => {
    try {
      const result = await ApiClient.users.deleteRoleAssignment({
        roleAssignment: roleAssignment.id,
      });
      if (result.success) {
        enqueueSnackbar("Successfully removed role.", {
          variant: "success",
          autoHideDuration: 3000,
        });
        onDelete(roleAssignment.id);
      } else {
        enqueueSnackbar(result.message || "Failed to remove role", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.error("Error removing role:", error);
      enqueueSnackbar("Error removing role. Please try again.", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const [expanded, setExpanded] = useState(false);

  const [subjectsDialogOpen, setSubjectsDialogOpen] = useState(false);

  const [educationLevelsDialogOpen, setEducationLevelsDialogOpen] =
    useState(false);

  const sortedSubjects = useMemo(() => {
    return [...availableSubjects].sort((a, b) => a.name.localeCompare(b.name));
  }, [availableSubjects]);

  const formatRoleName = () => {
    const roleName = roleAssignment?.role?.name || "Unknown Role";

    if (roleAssignment.school?.name) {
      return `${roleName} (School: ${roleAssignment.school.name})`;
    }

    if (roleAssignment.schoolGroup?.name) {
      return `${roleName} (Group: ${roleAssignment.schoolGroup.name})`;
    }

    return roleName;
  };

  const removeSubject = (subjectId) => {
    const updatedSubjects = selectedSubjects.filter(
      (subject) => subject.id !== subjectId,
    );
    updateRoleAssignment(updatedSubjects, selectedEducationLevels);
  };

  const removeEducationLevel = (levelId) => {
    const updatedLevels = selectedEducationLevels.filter(
      (level) => level.id !== levelId,
    );
    updateRoleAssignment(selectedSubjects, updatedLevels);
  };

  const handleSubjectsChange = (newSubjects) => {
    updateRoleAssignment(newSubjects, selectedEducationLevels);
  };

  const handleEducationLevelsChange = (newLevels) => {
    updateRoleAssignment(selectedSubjects, newLevels);
  };

  return (
    <>
      <AreYouSureDialog
        open={dialogOpen}
        actionText={"you want to delete this role"}
        setOpen={setDialogOpen}
        onDelete={deleteRoleAssignment}
        title={"Delete Role"}
      />
      <Box sx={{ mt: 2 }}>
        <Accordion expanded={expanded} variant="outlined">
          <AccordionSummary
            expandIcon={<ExpandMore />}
            onClick={() => setExpanded(!expanded)}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                pr: 1,
              }}
            >
              <Typography>{formatRoleName()}</Typography>

              <Box sx={{ ml: "auto", mr: 1 }}>
                <IconButton
                  size="small"
                  sx={{ border: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDialogOpen(true);
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <Box sx={{ mb: 1 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography variant="body1" color="text.primary">
                  Subjects
                </Typography>
              </Stack>

              <Stack
                direction="row"
                alignItems={"center"}
                spacing={1}
                flexWrap="wrap"
                useFlexGap
              >
                {selectedSubjects.map((subject) => (
                  <Chip
                    key={subject.id}
                    label={subject.name}
                    onDelete={() => removeSubject(subject.id)}
                  />
                ))}

                <IconButton
                  size="small"
                  sx={{ border: 0 }}
                  onClick={() => setSubjectsDialogOpen(true)}
                >
                  <AddCircle color={"success"} fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            <Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography variant="body1" color="text.primary">
                  Education Levels
                </Typography>
              </Stack>

              <Stack
                direction="row"
                alignItems={"center"}
                spacing={1}
                flexWrap="wrap"
                useFlexGap
              >
                {selectedEducationLevels.map((level) => (
                  <Chip
                    key={level.id}
                    label={level.level}
                    onDelete={() => removeEducationLevel(level.id)}
                  />
                ))}
                <IconButton
                  size="small"
                  sx={{ border: 0 }}
                  onClick={() => setEducationLevelsDialogOpen(true)}
                >
                  <AddCircle color={"success"} fontSize="medium" />
                </IconButton>
              </Stack>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <MultiSelectDialog
        open={subjectsDialogOpen}
        onClose={() => setSubjectsDialogOpen(false)}
        title="Select subjects"
        options={sortedSubjects}
        selected={selectedSubjects}
        getOptionLabel={(option) => option.name}
        onSave={handleSubjectsChange}
      />

      <MultiSelectDialog
        open={educationLevelsDialogOpen}
        onClose={() => setEducationLevelsDialogOpen(false)}
        title="Select education levels"
        options={availableEducationLevels}
        selected={selectedEducationLevels}
        getOptionLabel={(option) => option.level}
        onSave={handleEducationLevelsChange}
      />
    </>
  );
};
